import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { getStrategySession } from '../services/api'

// CloudFront serves the SPA shell for any /strategy/* request whose signed
// cookie is missing or expired (the distribution-wide 403 fallback rewrites
// to index.html). Landing on this route means exactly that. Re-mint the
// cookies via /strategy-session and send the browser back to the page it
// wanted — the user never sees more than a brief spinner.
//
// Two guards keep this from looping:
// - A path outside the caller's own audience prefix is never retried (their
//   cookie can't ever satisfy it); they land at their bundle root instead.
// - A path that still falls back right after a re-mint (deleted page, stale
//   link) is remembered in sessionStorage and shown an honest message.

const RETRY_KEY = 'strategy-reentry'
const RETRY_WINDOW_MS = 60_000

function recentlyRetried(path: string): boolean {
  try {
    const raw = sessionStorage.getItem(RETRY_KEY)
    if (!raw) return false
    const { p, t } = JSON.parse(raw)
    return p === path && Date.now() - t < RETRY_WINDOW_MS
  } catch {
    return false
  }
}

function markRetried(path: string) {
  try {
    sessionStorage.setItem(RETRY_KEY, JSON.stringify({ p: path, t: Date.now() }))
  } catch {
    /* private-mode storage failures just lose loop protection */
  }
}

export default function StrategyReentry() {
  const { isAuthenticated, isLoading } = useAuth()
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    if (isLoading || !isAuthenticated) return
    let cancelled = false

    const wanted = window.location.pathname + window.location.search

    if (recentlyRetried(wanted)) {
      setFailed('That strategy page isn’t available. It may have moved or been removed in the latest deploy.')
      return
    }

    const reenter = async () => {
      try {
        const session = await getStrategySession()
        if (cancelled) return
        markRetried(wanted)
        const prefix = `/strategy/${session.audience}/`
        // Full navigation (not SPA routing) so CloudFront serves the Astro
        // page with the fresh cookies attached.
        window.location.replace(wanted.startsWith(prefix) ? wanted : session.url)
      } catch (err) {
        console.error('Strategy re-entry failed:', err)
        if (!cancelled) setFailed('Could not restore your strategy session.')
      }
    }
    void reenter()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, isLoading])

  async function goToStrategyHome() {
    try {
      const session = await getStrategySession()
      window.location.assign(session.url)
    } catch (err) {
      console.error('Failed to open strategy home:', err)
    }
  }

  if (failed) {
    return (
      <div className="loading">
        <p>{failed}</p>
        <p>
          <button type="button" className="link-button" onClick={goToStrategyHome}>
            Strategy home
          </button>
          {' · '}
          <Link to="/">Back to the dashboard</Link>
        </p>
      </div>
    )
  }

  return <div className="loading">Restoring your strategy session…</div>
}
